import React, { useCallback, useEffect, useState } from "react";
import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import PaymentsAppLayout from "../../Components/Molecules/PaymentsAppLayout";
import ApplicationAccepted from "../../Assets/GettingStartedPage/ApplicationAccepted.svg";
import ApplicationUnderReview from "../../Assets/GettingStartedPage/ApplicationUnderReview.svg";
import ApplicationStatusBallDecor from "../../Assets/GettingStartedPage/ApplicationStatusBallDecor.svg";
import GettingStartedStep from "../../Components/Molecules/GettingStarted/GettingStartedStep";
import * as FingerprintLottie from "../../Assets/GettingStartedPage/FingerprintLottie.json";
import * as PuzzleLottie from "../../Assets/GettingStartedPage/PuzzleLottie.json";
import * as SettingsLottie from "../../Assets/GettingStartedPage/SettingsLottie.json";
import PrivateComponentLoadingPage from "../LoadingPage/PrivateComponentLoadingPage";
import { useOnboardingStore } from "../../Store/Onboarding";
import { PaymentRoutes } from "../../Contracts/Routes";
import { Navigate, useLocation } from "react-router-dom";
import { useTeamStore } from "../../Store/Team";

const GettingStarted: React.FC = () => {
  const location = useLocation();
  const [applicationBeingReviewedModalOpen, setApplicationBeingReviewedModalOpen] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const isFromHostedOnboarding = searchParams.get("fromHostedOnboarding") === "yes";
    setApplicationBeingReviewedModalOpen(isFromHostedOnboarding);
  }, [location.search]);
  const modalRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        setApplicationBeingReviewedModalOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modalRef]);

  const { adyenKYCDetails, getAdyenKYCDetails, isOnboardingComplete } = useOnboardingStore();
  const { getCurrentTeam } = useTeamStore();
  const { getAccessTokenSilently } = useAuth0();
  const refreshThings = useCallback(async () => {
    await Promise.all([
      getAdyenKYCDetails(getAccessTokenSilently),
      getCurrentTeam(getAccessTokenSilently)
    ]);
  }, [getAdyenKYCDetails, getCurrentTeam, getAccessTokenSilently]);
  useEffect(() => {
    refreshThings();
  }, [refreshThings]);

  const isOnboardingCompleted = isOnboardingComplete();
  if (isOnboardingCompleted) {
    return <Navigate to={PaymentRoutes.CUSTOMERS} />;
  }

  return (
    <PaymentsAppLayout>
      <div className="relative">
        {applicationBeingReviewedModalOpen && (
          <div className="z-40">
            <div className="fixed top-0 left-0 flex items-center justify-center w-screen h-[44rem] opacity-100 z-20">
              <div
                ref={modalRef}
                className="bg-white flex flex-col rounded shadow-lg max-w-[24rem] p-8"
              >
                <div className="flex flex-col space-y-4">
                  <img
                    src={ApplicationUnderReview}
                    alt="Application under review"
                    className="w-36 h-36 self-center"
                  />
                  <div className="font-semibold text-lg">Submission Under Review</div>
                  <div>
                    Your submission is being reviewed. Approval usually takes 2-4 business days.
                    We'll let you know as soon as your customer identification is approved.
                  </div>
                </div>
                <button
                  className="mt-6 px-4 py-1 bg-blue-600 rounded hover:bg-blue-500 text-white self-end"
                  onClick={() => setApplicationBeingReviewedModalOpen(false)}
                >
                  Got it!
                </button>
              </div>
            </div>
            <div className="fixed top-0 left-0 w-screen h-screen z-10 flex items-center justify-center bg-gray-950 opacity-20" />
          </div>
        )}
        {adyenKYCDetails?.is_kyc_completed && (
          <div className="bg-[#F0F5FF] grid md:grid-cols-5 relative">
            <div className="invisible lg:visible col-span-2 items-center flex">
              <img
                className="hidden lg:block"
                src={ApplicationAccepted}
                alt="Customer identification accepted"
              />
            </div>
            <div className="py-6 text-center mx-auto md:max-w-[22rem] col-span-1">
              <div className="text-gray-950 font-semibold mb-2 text-lg">
                Congratulations! Your Customer Identification Is Approved
              </div>
              <div className="text-gray-700">
                Your customer identification has been approved. Now you can receive payouts directly
                to your bank account
              </div>
            </div>
            <img
              className="invisible lg:visible absolute top-0 right-0 col-span-1"
              src={ApplicationStatusBallDecor}
              alt="Approved celebration"
            />
          </div>
        )}

        {adyenKYCDetails?.is_kyc_started && !adyenKYCDetails?.is_kyc_completed && (
          <div className="bg-[#F0F5FF] grid md:grid-cols-5 relative">
            <div className="invisible lg:visible col-span-2 items-center flex">
              <img className="hidden lg:block" src={ApplicationUnderReview} alt="Under review" />
            </div>
            <div className="py-6 text-center mx-auto md:max-w-[22rem] col-span-1">
              <div className="text-gray-950 font-semibold mb-2 text-md">
                Customer Identification Under Review
              </div>
              <div className="text-gray-700">
                Your submission is under review, approval usually takes 2-4 business days. We'll
                notify you when approved
              </div>
            </div>
            <img
              className="invisible lg:visible absolute top-0 right-0 col-span-1"
              src={ApplicationStatusBallDecor}
              alt="Under review"
            />
          </div>
        )}

        <div className="flex flex-col items-center justify-center">
          <div className="mt-8 text-3xl font-bold">Welcome to Payments</div>
          <div className="mt-2">How would you like to start?</div>
          <div className="mt-12 font-semibold text-lg">
            Continue to Setup and Customize your Account
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 mx-auto mt-20 md:grid-cols-3 cursor-pointer max-w-[82rem]">
          <GettingStartedStep
            title="Complete Your KYC"
            description="Complete your verification to start accepting payments"
            animationData={FingerprintLottie}
            navigateTo={PaymentRoutes.ONBOARDING_FORM}
          />
          <GettingStartedStep
            title="Connect Accounting Software"
            description="Manage invoice collection for all your clients at one place"
            animationData={PuzzleLottie}
            navigateTo={PaymentRoutes.SETTINGS_INTEGRATION}
          />
          <GettingStartedStep
            title="Customize your account settings now"
            description="Configure your email, payment, and payout preferences"
            animationData={SettingsLottie}
            navigateTo={PaymentRoutes.SETTINGS_CONFIG}
          />
        </div>
      </div>
    </PaymentsAppLayout>
  );
};

export default withAuthenticationRequired(GettingStarted, {
  onRedirecting: () => <PrivateComponentLoadingPage />
});
